import { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import api from "../../../services/api";
import { Grid, Paper, Button, Text, ButtonGroup} from "../../core";
import { SeccionHeader, NewLessonStepButton, ContentWrapper } from "../../patterns";
import { AppContext } from "../../../contexts/AppContext";
import { Box } from "@material-ui/core";
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

export default function LessonStudio() {

   let { id } = useParams();
   const { setLoading } = useContext(AppContext)

   const [lesson, setLesson] = useState({
      lessonSteps: []
   })

   const [language, setLanguage] = useState('pt')

   const [selectedStep, setSelectedStep] = useState(0)

   useEffect(() => {
      loadLesson()
   }, [id, language])

   const loadLesson = () => {
      setLoading(true)
      return api.get(`admin/lessons/${id}?language=${language}`).then(response => {
         const { lesson } = response.data

         setLesson(lesson)
         if (lesson.lessonSteps?.length > 0) {
            setSelectedStep((current) => Math.min(current, lesson.lessonSteps.length - 1))
         } else {
            setSelectedStep(0)
         }
      }).catch(() => {
         setLesson({ lessonSteps: [] })
         setSelectedStep(0)
      }).finally(() => {
         setLoading(false)
      })
   }

   const saveLesson = (lesson) => {
      setLoading(true)
      return api.patch(`admin/lessons/${id}`, {
         lesson: {
            lessonSteps: lesson.lessonSteps.map(step => step.id)
         }
      }).catch(() => {
         // mantém estado local; erro de persistência não deve travar a UI
      }).finally(() => {
         setLoading(false)
      })
   }


   const handleAddLessonStep = () => {
      setLoading(true)
      api.post('admin/steps', { step: {} }).then(response => {
         const { step } = response.data

         const steps = [...lesson.lessonSteps]
         const insertAt = Math.min(selectedStep + 1, steps.length)
         steps.splice(insertAt, 0, step)

         const updatedLesson = {
            ...lesson,
            lessonSteps: steps
         }

         setLesson(updatedLesson)
         setSelectedStep(insertAt)
         return saveLesson(updatedLesson)
      }).catch(() => {
         // erro silencioso; loading é liberado no finally abaixo
      }).finally(() => {
         setLoading(false)
      })
   }

   const handleNextStep = () => {
      if (selectedStep + 1 != lesson.lessonSteps.length) {
         setSelectedStep(selectedStep + 1)
      }
   }

   const handlePreviousStep = () => {
      if (selectedStep != 0) {
         setSelectedStep(selectedStep - 1)
      }
   }

   const handleDeleteStep = (selectedStep) => {
      const updatedLesson = {
         ...lesson,
         lessonSteps: lesson.lessonSteps.filter(step => step.id != lesson.lessonSteps[selectedStep].id)
      }

      setLesson(updatedLesson)
      saveLesson(updatedLesson)
      selectedStep === 0 ? setSelectedStep(selectedStep + 1) : setSelectedStep(selectedStep - 1)
   }


   const handleCreateContent = ({ contentType, stepId }) => {
      if (!stepId) return

      setLoading(true)
      api.post(`/admin/lessons/${id}/steps/${stepId}/contents`, {
         content: {
            type: contentType.toLowerCase(),
            language: language
         }
      }).then(() => loadLesson()).catch(() => {
         setLoading(false)
      })
   }

   const currentStep = lesson.lessonSteps[selectedStep]

   return (
      <>
         <Grid container style={{ height: "100%", display: "flex", flexDirection: "column" }} >
            <SeccionHeader
               title={`${lesson?.title} - Passos da Lição`}
               newButton={true}
               deleteButton={true}
               deleteButtonAction={() => handleDeleteStep(selectedStep)}
               newButtonAction={handleAddLessonStep}
            />
            <Paper style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', padding: 40, marginBottom: '25px' }}>
               <Text style={{ fontSize: '17px', fontWeight: '600', marginBottom: '20px' }}>{`Passo ${selectedStep + 1} / ${lesson.lessonSteps.length}`}</Text>
               <ButtonGroup color="white" size="small" variant="contained">
                  <Button color={language === 'pt' ? "primary" : 'iherit'} variant={language === 'pt' ? "contained" : 'iherit'} onClick={() => setLanguage('pt')}>PT</Button>
                  <Button color={language === 'en' ? "primary" : 'iherit'} variant={language === 'en' ? "contained" : 'iherit'} onClick={() => setLanguage('en')}>EN</Button>
                  <Button color={language === 'es' ? "primary" : 'iherit'} variant={language === 'es' ? "contained" : 'iherit'} onClick={() => setLanguage('es')}>ES</Button>
               </ButtonGroup>
               {!currentStep && (
                  <Text style={{ marginTop: 10, marginBottom: 10, color: '#666' }}>
                     Adicione um passo com o botão &quot;Novo&quot; antes de incluir conteúdos.
                  </Text>
               )}
               <NewLessonStepButton
                  disabled={!currentStep}
                  handleSelect={(contentType) => handleCreateContent({ contentType, stepId: currentStep?.id })}
               />
               <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Box style={{ display: 'flex', width: '80px', height: '50px', justifyContent: 'flex-start', alignItems: 'center' }}>
                     <Button onClick={handlePreviousStep}><ArrowBackIcon fontSize="large" /></Button>
                  </Box>
                  <Box style={{ height: 667, width: 375, backgroundColor: '#000', borderRadius: 20, padding: 20, marginTop: '30px' }}>
                     <Box style={{ backgroundColor: '#FFF', height: '100%', overflowY: 'scroll', borderRadius: 10 }}>
                        <StepItem
                           lessonStep={currentStep}
                           selectedStep={selectedStep}
                           lesson={lesson}
                           setLesson={setLesson}
                           saveLesson={saveLesson}
                           loadLesson={loadLesson}
                           language={language}
                        />
                     </Box>
                  </Box>
                  <Box style={{ display: 'flex', width: '80px', height: '50px', justifyContent: 'flex-end', alignItems: 'center' }}>
                     <Button onClick={handleNextStep}><ArrowForwardIcon fontSize="large" /></Button>
                  </Box>
               </Box>
            </Paper>
         </Grid>
      </>
   );
}

const StepItem = ({ lessonStep, lesson, loadLesson, setLesson, saveLesson, selectedStep, language}) => {

   let { id } = useParams();
   const filteredContents = lessonStep?.contents?.filter(content => content.language === language)

   const handleDeleteContent = (contentId) => {
      api.delete(`/admin/lessons/${id}/steps/${lessonStep.id}/contents/${contentId}`).then(response => {
         loadLesson()
      })
   }

   const handleEditContent = (content) => {
      api.patch(`/admin/lessons/${id}/steps/${lessonStep.id}/contents/${content.id}`, { content }).then(response => {
         loadLesson()
      })
   }

   const handleMoveUpward = (content) => {
      
      
      if (filteredContents.indexOf(content) === 0 ) return
      const contents = lessonStep.contents

      const splicedContents = contents.splice(contents.indexOf(content) -1, Number.MAX_VALUE, content).filter(function(element) {return element !== content})
      const updatedContents = contents.concat(splicedContents)

      api.put(`/admin/lessons/steps/${lessonStep.id}`, { updatedContents }).then(response => {
         loadLesson()
      })
   }

   const handleMoveDownward = (content) => {
      
      if (filteredContents.indexOf(content) === filteredContents.length -1) return
      const contents = lessonStep.contents

      const splicedContent = contents.splice(contents.indexOf(content) +1, 1, content)
      contents.splice(contents.indexOf(content), 1, splicedContent[0])

      api.put(`/admin/lessons/steps/${lessonStep.id}`, { updatedContents: contents }).then(response => {
         loadLesson()
      })
   }

      
   return (
      <>
         {filteredContents?.map((content, index) =>
            <ContentWrapper index={index} content={content} handleDeleteContent={handleDeleteContent} handleEditContent={handleEditContent} handleMoveUpward={handleMoveUpward} handleMoveDownward={handleMoveDownward} />
         )}
      </>
   )
}
